<template>
  <div class="product container">
    <h1>{{ product.name }} ({{ product.reference }})</h1>
    <div class="columns">
      <div class="main-image-container column is-6">
        <div class="image-container columns is-multiline">
          <div
            v-if="product.media"
            :style="{ backgroundImage: `url(${product.media[activeImage].location})` }"
            class="main-image"
            data-target="modal"
            @click="toggleModal('modal is-active')" />
          <span
            class="prev"
            @click="prevImage">
            &lt;
          </span>
          <span
            class="next"
            @click="nextImage">
            &gt;
          </span>
          <div class="thumbnail-container">
            <div
              v-for="(image, index) in product.media"
              :key="image._id"
              :class="['thumbnail-image', (activeImage == index) ? 'active' : '']"
              @click="activateImage(index)">
              <img :src="image.location">
            </div>
          </div>
        </div>
      </div>
      <div class="column is-6">
        <table class="table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
          <thead>
            <tr>
              <td colspan="2">{{ $t('Producten.Eigenschappen') }}</td>
            </tr>
          </thead>
          <tbody>
            <tr v-if="product.brand">
              <td class="bold">{{ $t('Producten.brand') }}</td>
              <td class="italic">{{ product.brand }}</td>
            </tr>
            <tr v-if="product.type">
              <td class="bold">{{ $t('Producten.type') }}</td>
              <td class="italic">{{ product.type }}</td>
            </tr>
            <tr v-if="product.yearOfConstruction">
              <td class="bold">{{ $t('Producten.yearOfConstruction') }}</td>
              <td class="italic">{{ product.yearOfConstruction }}</td>
            </tr>
            <tr v-if="product.hours">
              <td class="bold">{{ $t('Producten.hours') }}</td>
              <td class="italic">{{ product.hours }}</td>
            </tr>
            <tr v-if="product.capacity">
              <td class="bold">{{ $t('Producten.capacity') }}</td>
              <td class="italic">{{ product.capacity }} {{ $t('Producten.kg') }}</td>
            </tr>
            <tr v-if="product.liftAltitude">
              <td class="bold">{{ $t('Producten.liftAltitude') }}</td>
              <td class="italic">{{ product.liftAltitude }} {{ $t('Producten.cm') }}</td>
            </tr>
            <tr v-if="product.driveThroughAltitude">
              <td class="bold">{{ $t('Producten.driveThroughAltitude') }}</td>
              <td class="italic">{{ product.driveThroughAltitude }} {{ $t('Producten.cm') }}</td>
            </tr>
            <tr v-if="product.weight">
              <td class="bold">{{ $t('Producten.weight') }}</td>
              <td class="italic">{{ product.weight }} {{ $t('Producten.kg') }}</td>
            </tr>
            <tr v-if="product.tracks">
              <td class="bold">{{ $t('Producten.tracks') }}</td>
              <td class="italic">{{ product.tracks }}%</td>
            </tr>
            <tr v-if="propertyPresentAndNotNull('sideSlider')">
              <td class="bold">{{ $t('Producten.sideSlider') }}</td>
              <td class="italic">
                <span class="icon">
                  <font-awesome-icon :icon="iconYesNo(product.sideSlider)" />
                </span>
              </td>
            </tr>
            <tr v-if="product.engineBrand">
              <td class="bold">{{ $t('Producten.engineBrand') }}</td>
              <td class="italic">{{ product.engineBrand }}</td>
            </tr>
            <tr v-if="product.cylinderCount">
              <td class="bold">{{ $t('Producten.cylinderCount') }}</td>
              <td class="italic">{{ product.cylinderCount }}</td>
            </tr>
            <tr v-if="product.bucketCount">
              <td class="bold">{{ $t('Producten.bucketCount') }}</td>
              <td class="italic">{{ product.bucketCount }}</td>
            </tr>
            <tr v-if="propertyPresentAndNotNull('joystickControl')">
              <td class="bold">{{ $t('Producten.joystickControl') }}</td>
              <td class="italic">
                <span class="icon">
                  <font-awesome-icon :icon="iconYesNo(product.joystickControl)" />
                </span>
              </td>
            </tr>
            <tr v-if="propertyPresentAndNotNull('extraTubing')">
              <td class="bold">{{ $t('Producten.extraTubing') }}</td>
              <td class="italic">
                <span class="icon">
                  <font-awesome-icon :icon="iconYesNo(product.extraTubing)" />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <table
          v-if="productDescription"
          class="table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
          <tbody>
            <tr>
              <td class="italic">{{ productDescription }}</td>
            </tr>
          </tbody>
        </table>
        <table
          v-if="product.price"
          class="table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
          <tbody>
            <tr>
              <td class="bold">{{ $t('Producten.price') }}</td>
              <td class="italic">&euro; {{ formatPrice(product.price) }}</td>
            </tr>
          </tbody>
        </table>
        <router-link :to="'/contact/'">
          <button class="buy">{{ $t('Producten.Koop_nu') }}</button>
        </router-link>
      </div>
    </div>
    <div
      id="modal"
      :class="showModal">
      <div class="modal-background" />
      <div class="modal-content">
        <div class="main-image-container">
          <div class="image-container columns is-multiline">
            <div
              v-if="product.media"
              :style="{ backgroundImage: `url(${product.media[activeImage].location})` }"
              class="main-image main-image-modal">
              <span
                class="prev"
                @click="prevImage">
                &lt;
              </span>
              <span
                class="next"
                @click="nextImage">
                &gt;
              </span>
            </div>
          </div>
        </div>
      </div>
      <button
        class="modal-close is-large"
        aria-label="close"
        @click="toggleModal('modal')" />
    </div>
  </div>
</template>

<script>

import { mapGetters, mapActions } from "vuex";

export default {
  name: "Product",
  props: {
    productId: {
      type: String,
      required: false,
      default: "0"
    },
    productName: {
      type: String,
      required: false,
      default: "0"
    }
  },
  data() {
    return {
      activeImage: 0,
      showModal: "modal"
    };
  },
  computed: {
    ...mapGetters({
      product: "getProduct",
      productCategories: "getProductCategories",
      currentLanguage: "getCurrentLanguage"
    }),
    productDescription() {
      if (Object.prototype.hasOwnProperty.call(this.product, "description")) {
        if (Object.prototype.hasOwnProperty.call(this.product.description[0], "text")) {
          return this.product.description[0].text;
        }
      }
      return false;
    }
  },
  watch: {
    currentLanguage() {
      this.fetchProduct(this.productId);
    }
  },
  created() {
    this.fetchProduct(this.productId);
  },
  methods: {
    ...mapActions({
      fetchProduct: "fetchProduct"
    }),
    getImagePath(image) {
      return image ? `https://vanhoutte-api.oakfields.be/assets/uploads/${image}` : "";
    },
    formatPrice(value) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    iconYesNo(value) {
      if (value === 0) return ["far", "check"];
      return ["far", "ban"];
    },
    propertyPresentAndNotNull(propertyName) {
      if (Object.prototype.hasOwnProperty.call(this.product, propertyName)) {
        if (this.product[propertyName] !== null) return true;
      }
      return false;
    },
    nextImage() {
      let active = this.activeImage + 1;
      if (active >= this.product.media.length) {
        active = 0;
      }
      this.activateImage(active);
    },
    prevImage() {
      let active = this.activeImage - 1;
      if (active < 0) {
        active = this.product.media.length - 1;
      }
      this.activateImage(active);
    },
    toggleModal($active) {
      this.showModal = $active;
    },
    activateImage(imageIndex) {
      this.activeImage = imageIndex;
    }
  }
};

</script>

<style scoped lang="scss">
@import "@/assets/css/style.scss";

.modal-content {
  width: 80%;
}

.main-image-modal {
  height: 80vh !important;
}

#modal {
  z-index: 100;
  .prev, .next {
    top: calc(50% - 37px);
  }
}

h2 {
  color: $secundary;
  font-size: 24px;
  margin-top: 10px;
  margin-bottom: 40px;
}

table td {
  width: 50%;
}

table thead tr {
  height: 50px;
  text-transform: uppercase;
  font-weight: 700;
  vertical-align: middle;
  background-color: #ddd;
  & td {
    vertical-align: middle;
  }
}
.main-image {
  display: block;
  width: 100%;
  height: 400px;
  margin-bottom: 20px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  background-color: #ddd;
  cursor: pointer;
  img {
    max-height: 400px;
  }
}

.bold {
  font-weight: 600;
}

.italic {
  font-style: italic;
}

.thumbnail-container {
  position: relative;
}

.prev {
  position: absolute;
  top: 30%;
  left: 20px;
  cursor: pointer;
  font-size: 45px;
  background: rgba(255,255,255,0.6);
  padding: 0 10px;
  z-index: 50;
}

.next {
  position: absolute;
  top: 30%;
  right: 20px;
  cursor: pointer;
  font-size: 45px;
  background: rgba(255,255,255,0.6);
  padding: 0 10px;
  z-index: 50;
}

.thumbnail-image {
  width: 130px;
  display: table-cell;
  cursor: pointer;
  padding: 5px;
  border: 1px solid #dbdbdb;
  vertical-align: middle;
  &.active img{
    border: 2px solid $secundary;
  }
}

.product {
  margin-top: 40px;
  margin-bottom: 80px;
}

.image-container {
  padding: 12px;
}

h1 {
  color: $secundary;
  font-size: 32px;
  margin-top: 10px;
  margin-bottom: 40px;
}

.main-image-container {
  position: relative
}

.products-label {
  font-weight: 800;
}

.mb10 {
  margin-bottom: 10px;
}

.product-image {
  margin-bottom: 10px;
}

.buy {
  background-color: #e0493e;
  text-align: center;
  padding: 20px;
  color: #FFF;
  text-transform: uppercase;
  font-weight: 800;
  font-size: 16px;
  cursor: pointer;
  float: right;
}

span.products-price {
    position: absolute;
    left: 20px;
    top: 20px;
    background: rgba(224,73,62, 0.8);
    padding: 5px 15px;
    color: #fff;
}

@media (max-width: 1087px) {
  .producten {
    margin: 40px 20px 80px;
  }
}

@media (max-width: 1087px) {
  .product {
    margin: 40px 20px 80px;
  }
}

</style>
